<script setup lang="ts">
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu'
import UserAvatar from '@/components/user/UserAvatar.vue'
import {
  BellIcon,
  BookTextIcon,
  BuildingIcon,
  ChevronDownIcon,
  CircleUserRoundIcon,
  ContactRoundIcon,
  GroupIcon,
  LogOutIcon,
  MegaphoneIcon,
  MenuIcon,
  QrCodeIcon,
  SettingsIcon,
  XIcon,
} from "@lucide/vue";
import {computed, ref} from "vue";
import { useI18n } from 'vue-i18n';
import HeaderNavItem from "@/components/HeaderNavItem.vue";

const { t } = useI18n();

const {user, helpUrl} = defineProps({
    user: {
        type: Object,
        required: true,
    },
    helpUrl: {
        type: String,
        required: true,
    },
    unreadNotifications: {
        type: Number,
        default: 0,
    },
    csrfToken: {
        type: String,
        default: '',
    },
});


const currentLocation = computed(() => window.location.href);
const whatsNewLabel = computed(() => t("What’s new"));

const navItems = computed(() => [
    {
        label: t('Events'),
        icon: ContactRoundIcon,
        href: route('backoffice::tour'),
        active: currentLocation.value.startsWith(route('backoffice::tour'))
    },
    {
        label: t('Catalog'),
        icon: GroupIcon,
        href: route('backoffice::exponent.index'),
        active: currentLocation.value.startsWith(route('backoffice::exponent.index'))
    },
    user.can['access-orgs'] && {
        label: t('Organizations'),
        icon: BuildingIcon,
        href: route('backoffice::organizations.index'),
        active: currentLocation.value.startsWith(route('backoffice::organizations.index'))
    },
    user.can['access-admin'] && {
        label: t('Admin'),
        icon: SettingsIcon,
        href: route('backoffice::admin::dashboard'),
        active: currentLocation.value.startsWith(route('backoffice::admin::dashboard'))
    }
]);

const navOpen = ref(false);
</script>

<template>
    <div class="tw:bg-zinc-800 tw:text-white">
        <div class="tw:py-2 tw:max-w-[1170px] tw:px-4 tw:md:px-8 tw:mx-auto">
            <div class="tw:flex tw:items-center tw:justify-between">
                <div class="tw:flex tw:items-center tw:gap-4">
                    <div class="tw:shrink-0 tw:-ml-2">
                        <HeaderNavItem :href="route('backoffice::dashboard')">
                            <img src="../../../public/images/logo.svg" class="tw:h-7"/>
                        </HeaderNavItem>
                    </div>

                    <ul class="tw:hidden tw:md:flex tw:items-center tw:gap-2">
                        <template v-for="item in navItems">
                            <li>
                                <HeaderNavItem :href="item.href" :active="item.active">
                                    <component :is="item.icon" class="tw:mr-1 tw:w-4 tw:h-4" />
                                    {{ item.label }}
                                </HeaderNavItem>
                            </li>
                        </template>
                    </ul>
                </div>

                <div class="tw:flex tw:items-center tw:gap-6">
                    <ul class="tw:flex tw:items-center tw:gap-2">
                        <li>
                            <HeaderNavItem :href="helpUrl" target="_blank">
                                <BookTextIcon  class="tw:w-4 tw:h-4"/>
                            </HeaderNavItem>
                        </li>
                        <li>
                            <HeaderNavItem :href="route('backoffice::barcode-order.index')" :active="currentLocation.startsWith(route('backoffice::barcode-order.index'))">
                                <QrCodeIcon class="tw:w-4 tw:h-4"/>
                                <span class="sr-only">{{ $t('Barcode orders') }}</span>
                            </HeaderNavItem>
                        </li>
                        <li>
                            <HeaderNavItem :href="route('backoffice::announcement.index')" :active="currentLocation.startsWith(route('backoffice::announcement.index'))" :title="whatsNewLabel">
                                <MegaphoneIcon class="tw:w-4 tw:h-4"/>
                                <span class="sr-only">{{ whatsNewLabel }}</span>
                            </HeaderNavItem>
                        </li>
                        <li>
                            <HeaderNavItem :href="route('backoffice::notification.index')" :active="currentLocation.startsWith(route('backoffice::notification.index'))">
                                <div class="tw:relative">
                                    <template v-if="unreadNotifications > 0">
                                        <span class="tw:absolute tw:-top-3 tw:-right-0 tw:translate-x-[50%] tw:min-w-6 tw:bg-red-500 tw:text-white tw:rounded-full tw:px-1 tw:py-0.5 tw:text-center tw:flex tw:items-center tw:justify-center tw:text-xs tw:border-2 tw:border-zinc-800">
                                            {{ unreadNotifications }}
                                        </span>
                                    </template>
                                    <BellIcon class="tw:w-4 tw:h-4"/>
                                </div>
                                <span class="sr-only">{{ $t('Notifications') }}</span>
                            </HeaderNavItem>
                        </li>
                        <li>
                            <DropdownMenu>
                                <DropdownMenuTrigger class="tw:flex tw:items-center tw:px-2 tw:py-2 tw:cursor-pointer tw:text-zinc-300/80 tw:hover:text-zinc-200 tw:aria-expanded:text-white">
                                    <UserAvatar :user="user" :size="20"/>
                                    <ChevronDownIcon
                                                     class="tw:ml-2 tw:relative -tw:top-px tw:w-4 tw:h-4"/>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" side="bottom" class="tw:w-56" to="body">
                                    <DropdownMenuItem as-child class="tw:cursor-pointer">
                                      <a :href="route('backoffice::user-account::index')">
                                          <CircleUserRoundIcon class="tw:w-4 tw:h-4"/>
                                          {{ $t('My account') }}
                                      </a>
                                    </DropdownMenuItem>

                                    <form :action="route('logout')" method="POST">
                                        <input type="hidden" name="_token" :value="csrfToken">
                                        <DropdownMenuItem @select="(ev) => ev.preventDefault()" variant="destructive" as-child class="tw:cursor-pointer">
                                          <button type="submit">
                                              <LogOutIcon class="tw:w-4 tw:h-4"/>
                                              {{ $t('Logout') }}
                                          </button>
                                        </DropdownMenuItem>
                                    </form>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </li>
                    </ul>

                    <div class="tw:md:hidden">
                        <button class="tw:flex tw:items-center" @click="navOpen = !navOpen">
                            <template v-if="!navOpen">
                              <MenuIcon class="tw:w-5 tw:h-5"/>
                            </template>
                            <template v-else>
                              <XIcon class="tw:w-5 tw:h-5"/>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <template v-if="navOpen">
            <ul class="tw:md:hidden tw:items-center tw:gap-6 tw:px-4 tw:divide-y tw:divide-black/30 tw:border-y tw:border-black/30">
                <template v-for="item in navItems">
                    <li>
                        <a :href="item.href" class="tw:py-3 tw:flex tw:items-center tw:gap-1" :class="{'tw:text-white': item.active, 'tw:text-zinc-300/80 tw:hover:text-zinc-200': !item.active}">
                            <component :is="item.icon" class="tw:mr-1 tw:w-4 tw:h-4" />
                            {{ item.label }}
                        </a>
                    </li>
                </template>
            </ul>
        </template>
    </div>
</template>
